// ────────────────────────────────────────────────────────────────
// core/nav/nav-memory.service.ts — remembers the last URL visited per
// top-level nav section (path + query params). The left nav (shell.ts) reads
// this to link back to wherever the admin left a section — e.g. Customer 360
// reopens the same profile tab, Logs reopens with the same filters — instead
// of always resetting to the section's landing page. In-memory only: state
// resets on a full reload, same as every other feature store in the SPA.
// ────────────────────────────────────────────────────────────────

import { Injectable, signal } from '@angular/core';

export interface NavTarget {
  path: string;
  queryParams: Record<string, string>;
}

@Injectable({ providedIn: 'root' })
export class NavMemoryService {
  private readonly targets = signal<Record<string, NavTarget>>({});

  // Records `url` (path + optional "?query") under `prefix`, the nav item's
  // base route. Call once per NavigationEnd, for whichever nav item's route
  // prefixes the new URL.
  record(prefix: string, url: string): void {
    const [path, queryString] = url.split('?');
    const queryParams = queryString ? Object.fromEntries(new URLSearchParams(queryString)) : {};
    this.targets.update((m) => ({ ...m, [prefix]: { path, queryParams } }));
  }

  // The last-visited target for `prefix`, falling back to the bare route.
  linkFor(prefix: string): NavTarget {
    return this.targets()[prefix] ?? { path: prefix, queryParams: {} };
  }
}
